/**
 * Responsive Utilities
 * Helper functions for responsive design and class name management
 */

/**
 * Combine class names, filtering out falsy values
 * Similar to clsx/classnames libraries
 * @param classes - Array of class names or conditional class names
 * @returns Combined class name string
 */
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Responsive class helper
 * Generates responsive Tailwind classes based on breakpoint values
 *
 * @example
 * responsive({ base: 'text-sm', md: 'text-base', lg: 'text-lg' })
 * // Returns: 'text-sm md:text-base lg:text-lg'
 */
export function responsive(values: {
  base?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  '2xl'?: string;
}): string {
  const classes: string[] = [];

  if (values.base) classes.push(values.base);
  if (values.sm) classes.push(`sm:${values.sm}`);
  if (values.md) classes.push(`md:${values.md}`);
  if (values.lg) classes.push(`lg:${values.lg}`);
  if (values.xl) classes.push(`xl:${values.xl}`);
  if (values['2xl']) classes.push(`2xl:${values['2xl']}`);

  return classes.join(' ');
}

/**
 * Get responsive padding classes
 * @param mobile - Mobile padding (default: 4)
 * @param desktop - Desktop padding (default: 6)
 */
export function responsivePadding(mobile = 4, desktop = 6): string {
  return `p-${mobile} lg:p-${desktop}`;
}

/**
 * Get responsive gap classes
 * @param mobile - Mobile gap (default: 2)
 * @param desktop - Desktop gap (default: 4)
 */
export function responsiveGap(mobile = 2, desktop = 4): string {
  return `gap-${mobile} lg:gap-${desktop}`;
}

/**
 * Get responsive grid columns
 * @param mobile - Mobile columns (default: 1)
 * @param tablet - Tablet columns (default: 2)
 * @param desktop - Desktop columns (default: 4)
 */
export function responsiveGrid(
  mobile = 1,
  tablet = 2,
  desktop = 4
): string {
  return cn(
    `grid-cols-${mobile}`,
    tablet !== mobile && `md:grid-cols-${tablet}`,
    desktop !== tablet && `lg:grid-cols-${desktop}`
  );
}

/**
 * Check if touch device
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - legacy API
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Format bytes to human readable size
 * Useful for file upload components
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Debounce function for resize handlers
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}
